import { CSSProperties, useContext } from 'react';
import { GiTrophy } from 'react-icons/gi';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import HistoryIcon from '@mui/icons-material/History';
import PaidIcon from '@mui/icons-material/Paid';
import ReplayIcon from '@mui/icons-material/Replay';
import { Badge, Tooltip } from '@mui/material';
import { TABLE_TYPE_SITNGO, TABLE_TYPE_TOURNAMENT } from 'constants/gameConstants';
import GameContext from 'contexts/GameContext';
import { useGridMode } from 'contexts/GridModeContext';
import TableSettingsContext from 'contexts/TableSettingsContext';
import TexasChatContext from 'contexts/TexasChatContext';
import { useAppSelector } from 'hooks';
import { useHandHistory } from 'hooks/useHandHistory';
import { useIsFreezeout } from 'hooks/useIsFreezeout';
import { useOrientation } from 'hooks/useOrientation';
import { CHAT_TOGGLE, HISTORY_TOGGLE, panelToggleProps } from 'hooks/useOutsideTapDismiss';
import { useTableSelector } from 'hooks/useTableSelector';
import { findPlayerTournamentStatus, isAddOnOffered } from 'utils/addOnEligibility';

import MaintenanceMarquee from 'components/MaintenanceSchedule/MaintenanceMarquee';
import AddChipsButton from 'components/UserActions/TableActions/AddChipsButton';

import './UserActions.scss';

type TopActionProps = {
  actionContent?: React.ReactNode;
};

const TopAction = ({ actionContent }: TopActionProps) => {
  const userBalance = useAppSelector((state) => state.user.playerBankAmount);
  const isPlayerSeated = useTableSelector((table) => table.currentPlayer.isSeated);
  const playerGUID = useAppSelector((state) => state.user.playerGUID);

  const game = useTableSelector((table) => table.game);
  const { tabletype: tableType, schedule, status: gameStatus, gameID } = game;
  // A Freezeout never allows re-entry — hard-guard the re-entry button. Resolved
  // from the lobby lists (the format isn't carried in-game). See useIsFreezeout.
  const isFreezeout = useIsFreezeout(game?.tournamentId);
  const liveChatNotifications = useAppSelector((state) => state.liveChatNotifications);
  const isMaintenance = useAppSelector((state) => state.maintenanceSchedule.isMaintenance);
  const isGridMode = useGridMode();
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';

  const {
    showTournamentDetails,
    setShowTournamentDetails,
    canRebuyChips,
    confirmedAddOns,
    setOpenAddOnChips,
    setIsTimerOff,
    setOpenRebuyChips
  } = useContext(GameContext);
  const {
    isOpen: isOpenHandHistory,
    setIsOpen: setIsOpenHandHistory,
    data: handHistoryData
  } = useHandHistory();
  // An observer who has never sat down at this table has no history to open:
  // the button is disabled instead of opening a locked panel (product, internal
  // demo 2026-09-23). Seated players - even before their first blind - and
  // players who already qualified this session keep it enabled.
  const historyLocked = Boolean(handHistoryData?.locked);
  const historyDisabled = historyLocked && !isPlayerSeated;
  // Only a button that can toggle the panel is exempt from outside-dismissal;
  // a disabled one would otherwise swallow the tap that should close the panel.
  const historyTrigger = historyDisabled ? undefined : 'hand-history';
  const historyTooltip = historyDisabled
    ? 'Hand History unlocks when you sit down'
    : 'Hand History';
  const { isOpen: isOpenTexasChat, setIsOpen: setIsOpenTexasChat } = useContext(TexasChatContext);
  const { chatEnabled } = useContext(TableSettingsContext);
  // Unread chat count for the *current table*. Cleared (= 0) while the chat panel is
  // open, since the Chat component dispatches clearUnreadMessage on open and on each
  // new message arrival while it's mounted.
  const unreadChatCount = isOpenTexasChat
    ? 0
    : liveChatNotifications.find((f: any) => Number(f.gameID) === Number(gameID))
        ?.unreadMessageCount ?? 0;
  const tournamentStatus = useTableSelector((table) => table.tournamentStatus);
  const playerTournamentStatus = findPlayerTournamentStatus(
    tournamentStatus,
    game.tournamentId,
    playerGUID
  );

  // Whether to surface the Add-On button: the add-on window is open AND this
  // player still has an add-on left (EN-11059 — the table-wide gotTexas flag
  // alone kept it up for the rest of the break after the last purchase). See
  // utils/addOnEligibility for why each signal is trusted the way it is. We
  // deliberately do NOT require `gameStatus === 'P'`: the add-on window is the
  // BREAK, during which status is not 'P', so that check was hiding the button
  // at exactly the moment add-ons become available. Maintenance and a
  // non-empty balance still gate it.
  const canShowAddOn =
    isPlayerSeated &&
    userBalance >= 1 &&
    !isMaintenance &&
    isAddOnOffered({
      tableWindowOpen: (game as any).canAddOnChips,
      tableAddOnLimit: (game as any).addOn,
      playerStatus: playerTournamentStatus,
      confirmedAddOns,
      tournamentId: game.tournamentId
    });

  const handleOpenAddOnChipsModal = () => {
    setOpenAddOnChips(true);
    setIsTimerOff(false);
  };

  const handlecanRebuyChipsModal = () => {
    if (isMaintenance) return;
    setOpenRebuyChips(true);
    setIsTimerOff(false);
  };

  const toggleDetails = () => {
    // No explicit subscribe here: the player is AT this tournament table, so the
    // App-level centralized effect already holds the tournament feed via the
    // open-table rule. The info dashboard (TournamentDetails) it opens also
    // subscribes on its own while open. (Removed an unbalanced subscribe that
    // leaked the ref-count and blocked the tournament from ever unsubscribing.)
    setShowTournamentDetails(!showTournamentDetails);
  };

  const toggleHistory = () => {
    setIsOpenHandHistory((prev) => !prev);
    (setIsOpenTexasChat as any)(false);
  };

  const toggleChat = () => {
    (setIsOpenTexasChat as any)((prev: boolean) => !prev);
    setIsOpenHandHistory(false);
  };

  // In portrait: a single footer row.
  // Left group  = engagement icons (history + tournament-only utilities)
  // Center       = actions / pre-actions / join prompt
  // Right group  = chat
  // History and chat used to float in the corners ABOVE the footer; they now sit
  // in the footer row alongside the action buttons so everything lives on one bar.
  if (isPortrait) {
    return (
      <div style={portraitColumnStyle}>
        {/* Maintenance marquee sits ABOVE the action bar so it stays visible on
            small portrait screens (e.g. iPhone SE); the landscape branch renders
            its own copy. Grid cells suppress it — the grid shows one at its top. */}
        {!isGridMode && <MaintenanceMarquee />}
        <div style={portraitFooterRowStyle}>
          {/* Tournament-only utilities (add-on / re-entry / details) keep their
            original behavior: a vertical stack floating ABOVE the footer on the
            left — exactly where the hand-history icon used to sit (bottom: 100%,
            left: 0, ~0.65rem clearance), now anchored to the footer row itself so
            the trophy lands in that same spot rather than sitting on the bar. The
            trophy is last (bottom of the column) so it occupies the old history
            position; add-on / re-entry stack above it when present.
            EN-10420 width rule (Jason + Marlon huddle 8/13): whether the open
            bet tray covers this stack is the TRAY's call, decided by viewport
            width alone (resolveTrayWidthVariant, gameConstants). Below the flip
            point the full-bleed tray paints over the stack ON PURPOSE (tray
            zIndex 50 vs 15 — tournament details are not decision info during a
            15s bet window, and on a phone a competing tap target next to the
            bet controls costs more than it gives); at/above it the contained
            tray's side gutters keep the whole stack visible and tappable.
            Supersedes the 8/12 always-covered note (Jira 66054), which itself
            superseded the Unity-parity reading of the AC. Do NOT relocate the
            trophy inline into the footer row as a "fix": that widens the left
            icon group and shifts the tray's centering anchor off-center —
            Marlon caught that distortion in QA. */}
          <div style={portraitUtilStackStyle}>
            {canShowAddOn && (
              <Tooltip title='Add-On'>
                <button
                  onClick={handleOpenAddOnChipsModal}
                  style={mobileIconBtn}
                  aria-label='Add-On'
                  {...iconBtnHoverHandlers}>
                  <PaidIcon style={mobileIconSize} />
                </button>
              </Tooltip>
            )}
            {userBalance >= 1 &&
              tableType === TABLE_TYPE_TOURNAMENT &&
              schedule !== 'Rolling' &&
              canRebuyChips &&
              !isFreezeout &&
              gameStatus === 'P' && (
                <Tooltip title='Re-Entry'>
                  <button
                    onClick={handlecanRebuyChipsModal}
                    style={mobileIconBtn}
                    aria-label='Re-Entry'
                    {...iconBtnHoverHandlers}>
                    <ReplayIcon style={mobileIconSize} />
                  </button>
                </Tooltip>
              )}
            {(tableType === TABLE_TYPE_TOURNAMENT || tableType === TABLE_TYPE_SITNGO) && (
              <Tooltip title='Tournament Details'>
                <button
                  onClick={toggleDetails}
                  style={mobileIconBtn}
                  aria-label='Tournament details'
                  {...iconBtnHoverHandlers}>
                  <GiTrophy style={mobileIconSize} />
                </button>
              </Tooltip>
            )}
            {/* Per-table Add Chips ("+") — grid-only and cash-only; it self-gates
              and renders null otherwise. Last in the stack so it sits at the
              bottom, directly above the hand-history icon below. */}
            <AddChipsButton />
          </div>

          {/* Left group — only the history icon sits inline in the footer (the
            corner anchor, per the Mobile.html design). The table top-up "+" was
            removed here: Add Chips now lives in the header's top-right stack. */}
          <div style={portraitLeftGroupStyle}>
            {/* The span keeps the tooltip working while the button is disabled. */}
            <Tooltip title={historyTooltip}>
              <span style={disabledTooltipAnchorStyle}>
                <button
                  onClick={toggleHistory}
                  data-comms-panel-trigger={historyTrigger}
                  {...panelToggleProps(HISTORY_TOGGLE)}
                  disabled={historyDisabled}
                  style={{
                    ...mobileIconBtn,
                    ...(isOpenHandHistory ? activeBtnStyle : {}),
                    ...(historyDisabled ? disabledIconBtnStyle : {})
                  }}
                  aria-label='Hand history'
                  {...(historyDisabled ? {} : iconBtnHoverHandlers)}>
                  <HistoryIcon style={mobileIconSize} />
                </button>
              </span>
            </Tooltip>
          </div>

          {/* Center: actions / pre-actions / join prompt. flex:1 + minWidth:0 so it
            takes the room between the icon groups and lets its buttons shrink to
            fit rather than pushing the icons off-screen. */}
          <div style={portraitActionsStyle}>{actionContent || <div />}</div>

          {/* Right group — chat (the right corner anchor, per the Mobile.html
            design). Table controls (Exit/Sit Out/Observe) moved to the header's
            top-right stack — see TableHeader portrait branch. */}
          <div style={portraitRightGroupStyle}>
            {chatEnabled && (
              <Tooltip title='Chat'>
                <button
                  onClick={toggleChat}
                  data-comms-panel-trigger='chat'
                  style={{ ...mobileIconBtn, ...(isOpenTexasChat ? activeBtnStyle : {}) }}
                  aria-label='Chat'
                  {...panelToggleProps(CHAT_TOGGLE)}
                  {...iconBtnHoverHandlers}>
                  <Badge
                    color='error'
                    badgeContent={unreadChatCount}
                    max={99}
                    overlap='circular'
                    sx={chatBadgeSx}>
                    <ChatBubbleOutlineIcon style={mobileIconSize} />
                  </Badge>
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Landscape: three-section layout — icons left, actions center, space right
  return (
    <div style={{ width: '100%' }}>
      <div style={taskbarStyle}>
        {/* Left: utility icons (inset from the edge, toward the center) */}
        <div style={{ ...sectionStyle, marginLeft: '2rem' }}>
          <Tooltip title={historyTooltip}>
            <span style={disabledTooltipAnchorStyle}>
              <button
                onClick={toggleHistory}
                data-comms-panel-trigger={historyTrigger}
                {...panelToggleProps(HISTORY_TOGGLE)}
                disabled={historyDisabled}
                aria-label='Hand history'
                style={{
                  ...iconBtnStyle,
                  ...(isOpenHandHistory ? activeBtnStyle : {}),
                  ...(historyDisabled ? disabledIconBtnStyle : {})
                }}
                {...(historyDisabled ? {} : iconBtnHoverHandlers)}>
                <HistoryIcon style={iconSize} />
              </button>
            </span>
          </Tooltip>
          {/* Table chip top-up ("Add Chips") moved to the floating Saloon header
              (see TableHeader compact+tableOverlay branch); the redundant "+" here
              has been removed. */}
          {canShowAddOn && (
            <Tooltip title='Add-On'>
              <button
                onClick={handleOpenAddOnChipsModal}
                style={iconBtnStyle}
                {...iconBtnHoverHandlers}>
                <PaidIcon style={iconSize} />
              </button>
            </Tooltip>
          )}
          {userBalance >= 1 &&
            tableType === TABLE_TYPE_TOURNAMENT &&
            schedule !== 'Rolling' &&
            canRebuyChips &&
            !isFreezeout &&
            gameStatus === 'P' && (
              <Tooltip title='Re-Entry'>
                <button
                  onClick={handlecanRebuyChipsModal}
                  style={iconBtnStyle}
                  {...iconBtnHoverHandlers}>
                  <ReplayIcon style={iconSize} />
                </button>
              </Tooltip>
            )}
          {(tableType === TABLE_TYPE_TOURNAMENT || tableType === TABLE_TYPE_SITNGO) && (
            <Tooltip title='Tournament Details'>
              <button
                onClick={toggleDetails}
                style={iconBtnStyle}
                aria-label='Tournament details'
                {...iconBtnHoverHandlers}>
                <GiTrophy style={iconSize} />
              </button>
            </Tooltip>
          )}
          {/* Sit Out / Stand Up / Exit cluster moved to the floating right rail
              in landscape single-table view — see Game.tsx (tableActionRail). */}
        </div>

        {/* Center: poker actions (absolutely centered on full width) */}
        <div style={centerSectionStyle}>{actionContent}</div>

        {/* Right: chat (flanks the centered action row, per the landscape design) */}
        <div style={{ ...sectionStyle, marginLeft: 'auto', marginRight: '2rem' }}>
          {chatEnabled && (
            <Tooltip title='Chat'>
              <button
                onClick={toggleChat}
                data-comms-panel-trigger='chat'
                style={{
                  ...iconBtnStyle,
                  ...(isOpenTexasChat ? activeBtnStyle : {})
                }}
                {...panelToggleProps(CHAT_TOGGLE)}
                {...iconBtnHoverHandlers}>
                <Badge
                  color='error'
                  badgeContent={unreadChatCount}
                  max={99}
                  overlap='circular'
                  sx={chatBadgeSx}>
                  <ChatBubbleOutlineIcon style={iconSize} />
                </Badge>
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      {/* Maintenance notification as the outlaw scrolling marquee (same look as
          the lobby). Single-table only — in grid view the marquee is rendered
          once at the grid level, not per cell. */}
      {!isGridMode && <MaintenanceMarquee />}
    </div>
  );
};

export default TopAction;

/* ── Portrait styles ── */

// Portrait root: stacks the maintenance marquee (when present) above the action
// bar. Full width so the marquee spans the footer band.
const portraitColumnStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%'
};

// Single footer row: [left icons] [actions] [right icons]. The icon groups are
// fixed-width (flex-shrink 0); the action slot flexes to fill what's left.
const portraitFooterRowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: '0.25rem',
  minHeight: '54px',
  // Anchor for the tournament-utility stack that floats above the footer.
  position: 'relative'
};

const portraitSideGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '0.25rem',
  flexShrink: 0,
  zIndex: 15,
  pointerEvents: 'auto'
};

const portraitLeftGroupStyle: CSSProperties = {
  ...portraitSideGroupStyle,
  justifyContent: 'flex-start'
};

// Tournament-only utilities (add-on / re-entry / details) stacked vertically and
// floating above the footer, anchored to the footer row (not the history button)
// so the bottom icon lands exactly where the hand-history icon floated before the
// redesign: bottom: 100% + left: 0 + 0.65rem clearance. Absolutely positioned so
// it adds no width/height to the footer row.
const portraitUtilStackStyle: CSSProperties = {
  position: 'absolute',
  bottom: '100%',
  left: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  alignItems: 'flex-start',
  paddingBottom: '0.65rem',
  zIndex: 15,
  pointerEvents: 'auto'
};

const portraitRightGroupStyle: CSSProperties = {
  ...portraitSideGroupStyle,
  justifyContent: 'flex-end'
};

const portraitActionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Take the space between the icon groups and let the action buttons shrink to
  // fit (minWidth:0 lets flex children narrow below their content width).
  flex: 1,
  minWidth: 0,
  gap: '0.25rem',
  minHeight: '54px',
  // Query container so the action CTAs (e.g. the long guest "Login or Sign up
  // to join") can scale to THIS slot's width via cqi units, rather than the
  // viewport — otherwise a wide screen keeps them full-size and they overflow
  // into the flanking history/chat icons.
  containerType: 'inline-size'
};

// Saloon ".tp-iconbtn" — leather-on-ink square with an orange edge, matching the
// landscape iconBtnBaseStyle and the portrait design's .tp-iconbtn. Kept at a
// 44px touch target (design uses 38px on desktop-mobile, but mobile needs more).
// Static box-shadow per the perf guide; only transform/color animate.
const mobileIconBtn: CSSProperties = {
  width: '44px',
  height: '44px',
  minWidth: '44px',
  minHeight: '44px',
  background: 'linear-gradient(180deg, rgba(74,35,10,0.55), rgba(40,18,6,0.65))',
  border: '1px solid rgba(232,130,31,0.3)',
  borderRadius: '8px',
  color: '#d9c79e',
  padding: 0,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'inherit',
  boxShadow: '0 3px 8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,180,92,0.1)',
  transition: 'transform 0.15s ease, color 0.15s ease, border-color 0.15s ease'
};

const mobileIconSize: CSSProperties = {
  fontSize: '1.4rem'
};

/* ── Landscape styles ── */

const taskbarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: '0.5rem',
  position: 'relative',
  // Tall enough to fully contain the absolutely-centered Join/poker action
  // button (52px) so it sits within the bottom bar instead of poking above it.
  minHeight: '56px'
};

const sectionStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  flexShrink: 0
};

const centerSectionStyle: CSSProperties = {
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'auto',
  zIndex: 2
};

// Saloon ".tp-iconbtn" — leather-on-ink square with an orange edge. Static
// box-shadow (perf guide); hover only shifts color/border + scales.
const iconBtnBaseStyle: CSSProperties = {
  width: 44,
  height: 44,
  background: 'linear-gradient(180deg, rgba(74,35,10,0.55), rgba(40,18,6,0.65))',
  border: '1px solid rgba(232,130,31,0.3)',
  borderRadius: '8px',
  color: '#d9c79e',
  padding: 0,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'inherit',
  boxShadow: '0 3px 8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,180,92,0.1)',
  transition: 'transform 0.15s ease, color 0.15s ease, border-color 0.15s ease'
};

// Disabled = dimmed, no shadow, no press affordance. The hover handlers are not
// attached while disabled, so it never brightens or scales.
const disabledIconBtnStyle: CSSProperties = {
  opacity: 0.4,
  cursor: 'default',
  boxShadow: 'none'
};

// MUI's Tooltip listens on its child; a disabled <button> emits no events, so
// the button sits inside a span that does.
const disabledTooltipAnchorStyle: CSSProperties = {
  display: 'inline-flex'
};

const activeBtnStyle: CSSProperties = {
  background: 'rgba(233, 140, 38, 0.2)',
  color: '#E98C26'
};

const chatBadgeSx = {
  '& .MuiBadge-badge': {
    minWidth: 16,
    height: 16,
    fontSize: '0.6rem',
    fontWeight: 700,
    padding: '0 4px',
    top: 2,
    right: 2,
    border: '1.5px solid rgba(15, 25, 35, 0.95)',
    boxShadow: '0 0 6px rgba(196, 32, 33, 0.6)'
  }
};

const iconBtnStyle: CSSProperties = iconBtnBaseStyle;

const iconBtnHoverHandlers = {
  onMouseEnter: (e: React.MouseEvent) => {
    (e.currentTarget as HTMLElement).style.color = '#ffb45c';
    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(232,130,31,0.6)';
    (e.currentTarget as HTMLElement).style.transform = 'scale(1.08)';
  },
  onMouseLeave: (e: React.MouseEvent) => {
    (e.currentTarget as HTMLElement).style.color = '#d9c79e';
    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(232,130,31,0.3)';
    (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
  }
};

const iconSize: CSSProperties = {
  fontSize: '1.25rem'
};
